import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
    ArrowLeft, Sun, Cloud, CloudRain, CloudLightning, Snowflake, Wind,
    Droplets, Thermometer, Sunrise, Sunset, Umbrella,
    Activity, MapPin, Waves
} from 'lucide-react';
import LocationMap from '../components/common/LocationMap';

// ─── Wetter-Hilfsfunktionen ────────────────────────────────────────────────────

const getWeatherIcon = (code, size = 24) => {
    const className = 'shrink-0';
    if (code === 0 || code === 1) return <Sun size={size} className={`text-amber-400 ${className}`} />;
    if (code === 2 || code === 3) return <Cloud size={size} className={`text-slate-300 ${className}`} />;
    if (code >= 45 && code <= 48) return <Wind size={size} className={`text-slate-400 ${className}`} />;
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain size={size} className={`text-blue-400 ${className}`} />;
    if (code >= 71 && code <= 77) return <Snowflake size={size} className={`text-sky-200 ${className}`} />;
    if (code >= 95 && code <= 99) return <CloudLightning size={size} className={`text-purple-400 ${className}`} />;
    return <Cloud size={size} className={`text-slate-300 ${className}`} />;
};

const getWeatherEmoji = (code) => {
    if (code === 0 || code === 1) return '☀️';
    if (code === 2 || code === 3) return '⛅';
    if (code >= 45 && code <= 48) return '🌫️';
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return '🌧️';
    if (code >= 71 && code <= 77) return '❄️';
    if (code >= 95 && code <= 99) return '⛈️';
    return '🌤️';
};

const getWeatherDescription = (code) => {
    if (code === 0) return 'Klarer Himmel';
    if (code === 1) return 'Überwiegend klar';
    if (code === 2) return 'Teilweise bewölkt';
    if (code === 3) return 'Bedeckt';
    if (code >= 45 && code <= 48) return 'Neblig';
    if (code >= 51 && code <= 55) return 'Nieselregen';
    if (code >= 61 && code <= 65) return 'Regen';
    if (code >= 66 && code <= 67) return 'Gefrierender Regen';
    if (code >= 71 && code <= 77) return 'Schneefall';
    if (code >= 80 && code <= 82) return 'Regenschauer';
    if (code >= 85 && code <= 86) return 'Schneeschauer';
    if (code >= 95 && code <= 99) return 'Gewitter';
    return 'Bewölkt';
};

const getBackgroundGradient = (code) => {
    if (code === 0 || code === 1) return 'from-amber-500 via-orange-400 to-sky-500';
    if (code === 2 || code === 3) return 'from-slate-600 via-slate-500 to-slate-400';
    if (code >= 45 && code <= 48) return 'from-gray-600 via-gray-500 to-gray-400';
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'from-blue-800 via-blue-600 to-indigo-500';
    if (code >= 71 && code <= 77) return 'from-sky-400 via-sky-300 to-slate-200';
    if (code >= 95 && code <= 99) return 'from-indigo-950 via-purple-900 to-slate-800';
    return 'from-sky-500 via-sky-400 to-blue-300';
};

const uvLabel = (index) => {
    if (index <= 2) return { label: 'Niedrig', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (index <= 5) return { label: 'Mäßig', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    if (index <= 7) return { label: 'Hoch', color: 'text-orange-500', bg: 'bg-orange-500/20' };
    if (index <= 10) return { label: 'Sehr hoch', color: 'text-rose-500', bg: 'bg-rose-500/20' };
    return { label: 'Extrem', color: 'text-purple-500', bg: 'bg-purple-500/20' };
};

// ─── Custom Tooltip für Recharts ──────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label, chartType }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const unit = chartType === 'temp' ? '°C' : chartType === 'precip' ? '%' : chartType === 'wind' ? ' km/h' : '%';
        const icon = chartType === 'temp' ? <Thermometer size={14}/> : chartType === 'precip' ? <Umbrella size={14}/> : chartType === 'wind' ? <Wind size={14}/> : <Droplets size={14}/>;
        
        return (
            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border border-white/20 dark:border-white/10 p-3 rounded-2xl shadow-xl">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{label}:00 Uhr</p>
                <div className="flex items-center gap-2">
                    <span className="text-plantlix-500">{icon}</span>
                    <span className="text-sm font-black text-gray-900 dark:text-white">
                        {payload[0].value}{unit}
                    </span>
                </div>
                <p className="text-[9px] font-bold text-gray-500 dark:text-gray-400 mt-1">
                    {getWeatherDescription(data.weatherCode)}
                </p>
            </div>
        );
    }
    return null;
};

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────

const WeatherDayDetail = () => {
    const navigate = useNavigate();
    const { dayIndex } = useParams();
    const idx = parseInt(dayIndex ?? '0', 10);

    const [dayData, setDayData] = useState(null);
    const [hourly, setHourly] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartType, setChartType] = useState('temp'); // 'temp', 'precip', 'wind', 'humidity'
    const [coords, setCoords] = useState({ lat: 51.0504, lng: 13.7373 });

    useEffect(() => {
        fetchDayDetail();
        window.scrollTo(0, 0);
    }, [idx]);

    const fetchDayDetail = () => {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (pos) => loadData(pos.coords.latitude, pos.coords.longitude),
                () => loadData(51.0504, 13.7373), // Fallback Dresden
                { timeout: 6000, enableHighAccuracy: false }
            );
        } else {
            loadData(51.0504, 13.7373);
        }
    };

    const loadData = async (lat, lat_val, lon_val) => {
        // Fix potential naming conflict in parameters
        const actualLat = typeof lat === 'number' ? lat : lat_val;
        const actualLon = typeof lat === 'number' ? lat_val : lon_val;
        
        try {
            setCoords({ lat: actualLat, lng: actualLon });
            const url = `/api/weather/current/?latitude=${actualLat}&longitude=${actualLon}` +
                `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,uv_index_max,wind_speed_10m_max,precipitation_probability_max,sunrise,sunset` +
                `&hourly=temperature_2m,weather_code,precipitation_probability,wind_speed_10m,relative_humidity_2m,apparent_temperature` +
                `&timezone=auto`;

            const res = await fetch(url);
            if (!res.ok) throw new Error('API Fehler');
            const data = await res.json();

            const d = data.daily;
            const dayEntry = {
                isoDate: d.time[idx],
                date: new Date(d.time[idx]).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' }),
                shortDate: new Date(d.time[idx]).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }),
                weatherCode: d.weather_code[idx],
                tempMax: Math.round(d.temperature_2m_max[idx]),
                tempMin: Math.round(d.temperature_2m_min[idx]),
                precip: d.precipitation_sum[idx],
                uv: d.uv_index_max?.[idx] ?? null,
                windMax: d.wind_speed_10m_max?.[idx] ?? null,
                precipProb: d.precipitation_probability_max?.[idx] ?? null,
                sunrise: d.sunrise?.[idx] ?? null,
                sunset: d.sunset?.[idx] ?? null,
            };
            setDayData(dayEntry);

            const targetDate = d.time[idx];
            const hourlyEntries = (data.hourly?.time ?? [])
                .map((t, i) => ({
                    time: t,
                    hour: new Date(t).getHours(),
                    hourLabel: `${new Date(t).getHours()}`,
                    temp: Math.round(data.hourly.temperature_2m[i]),
                    apparentTemp: Math.round(data.hourly.apparent_temperature?.[i] ?? data.hourly.temperature_2m[i]),
                    weatherCode: data.hourly.weather_code[i],
                    precip: data.hourly.precipitation_probability?.[i] ?? 0,
                    wind: Math.round(data.hourly.wind_speed_10m?.[i] ?? 0),
                    humidity: data.hourly.relative_humidity_2m?.[i] ?? 0,
                }))
                .filter(e => e.time.startsWith(targetDate));

            setHourly(hourlyEntries);
            setLoading(false);
        } catch (err) {
            setError('Wetterdaten konnten nicht geladen werden.');
            setLoading(false);
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '–';
        return new Date(isoString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    const chartConfig = useMemo(() => {
        switch (chartType) {
            case 'temp': return { dataKey: 'temp', color: '#10b981', label: 'Temperatur (°C)' };
            case 'precip': return { dataKey: 'precip', color: '#3b82f6', label: 'Regen (%)' };
            case 'wind': return { dataKey: 'wind', color: '#94a3b8', label: 'Wind (km/h)' };
            case 'humidity': return { dataKey: 'humidity', color: '#06b6d4', label: 'Feuchtigkeit (%)' };
            default: return { dataKey: 'temp', color: '#10b981', label: 'Temp' };
        }
    }, [chartType]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white dark:bg-[#0f1318] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-plantlix-500/20 border-t-plantlix-500 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">Lade Plantlix Wetter...</p>
                </div>
            </div>
        );
    }

    if (error || !dayData) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#0f1318] flex items-center justify-center p-6">
                <div className="text-center max-w-xs mx-auto">
                    <Umbrella className="text-rose-500 mx-auto mb-6" size={40} />
                    <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Ups!</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">{error ?? 'Die Wetterdaten sind aktuell nicht erreichbar.'}</p>
                    <button onClick={() => navigate(-1)} className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-black shadow-lg">
                        Zurück
                    </button>
                </div>
            </div>
        );
    }

    const bgGradient = getBackgroundGradient(dayData.weatherCode);
    const uvInfo = dayData.uv !== null ? uvLabel(dayData.uv) : null;
    const isToday = idx === 0;

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#0f1318] font-inter">
            {/* ── Header Area ─────────────────────────────────────────────── */}
            <div className={`relative bg-gradient-to-br ${bgGradient} text-white pb-12 pt-6 px-6 overflow-hidden transition-all duration-700`}>
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                
                <div className="relative z-10 max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-10">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-3 bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/25 transition-all"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div className="px-5 py-2.5 bg-black/20 backdrop-blur-md rounded-2xl border border-white/10 text-xs font-black uppercase tracking-widest flex items-center gap-2">
                            <MapPin size={14} className="text-white/60" />
                            {isToday ? 'Standort (📍)' : 'Sachsen'}
                        </div>
                    </div>

                    <div className="flex flex-col items-center text-center mb-10">
                        <p className="text-white/80 text-sm font-black uppercase tracking-[0.2em] mb-4 drop-shadow-sm">{dayData.date}</p>
                        <div className="relative inline-block mb-4">
                            <span className="text-[120px] leading-none font-black tracking-tighter drop-shadow-2xl">
                                {dayData.tempMax}<span className="text-4xl align-top font-bold text-white/50 ml-1">°</span>
                            </span>
                            <div className="absolute -right-16 top-1/2 -translate-y-1/2 text-7xl animate-bounce-slow">
                                {getWeatherEmoji(dayData.weatherCode)}
                            </div>
                        </div>
                        <h2 className="text-2xl font-black text-white drop-shadow-md">{getWeatherDescription(dayData.weatherCode)}</h2>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <GlassCard icon={<Umbrella size={20} />} label="Regen" value={`${dayData.precipProb}%`} color="bg-blue-400" />
                        <GlassCard icon={<Droplets size={20} />} label="Menge" value={`${dayData.precip}mm`} color="bg-cyan-400" />
                        <GlassCard icon={<Wind size={20} />} label="Wind" value={`${Math.round(dayData.windMax)} km/h`} color="bg-slate-300" />
                        <GlassCard icon={<Sun size={20} />} label="UV-Index" value={dayData.uv} color={uvInfo?.color.replace('text-', 'bg-')} />
                    </div>
                </div>
            </div>

            {/* ── Graphical Section ───────────────────────────────────────── */}
            <div className="relative z-20 -mt-8 max-w-2xl mx-auto px-6 pb-20 space-y-6">
                
                <section className="bg-white dark:bg-[#1a1e26] rounded-[32px] p-6 shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-white/5 overflow-hidden">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                        <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest flex items-center gap-2">
                            <Activity size={16} className="text-plantlix-500" /> Tagesverlauf
                        </h3>
                        
                        <div className="flex bg-gray-100 dark:bg-white/5 p-1 rounded-xl">
                            <TabBtn active={chartType === 'temp'} onClick={() => setChartType('temp')} icon={<Thermometer size={14}/>} />
                            <TabBtn active={chartType === 'precip'} onClick={() => setChartType('precip')} icon={<Umbrella size={14}/>} />
                            <TabBtn active={chartType === 'wind'} onClick={() => setChartType('wind')} icon={<Wind size={14}/>} />
                            <TabBtn active={chartType === 'humidity'} onClick={() => setChartType('humidity')} icon={<Waves size={14}/>} />
                        </div>
                    </div>

                    <div className="h-[240px] w-full -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={hourly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={chartConfig.color} stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor={chartConfig.color} stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(156, 163, 175, 0.1)" />
                                <XAxis 
                                    dataKey="hourLabel" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 10, fontWeight: 'bold', fill: '#9ca3af'}}
                                    interval={3}
                                />
                                <YAxis hide domain={['auto', 'auto']} />
                                <Tooltip content={<CustomTooltip chartType={chartType} />} cursor={{ stroke: chartConfig.color, strokeWidth: 1, strokeDasharray: '5 5' }} />
                                <Area 
                                    type="monotone" 
                                    dataKey={chartConfig.dataKey} 
                                    stroke={chartConfig.color} 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorVal)" 
                                    animationDuration={1500}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="mt-8 flex gap-4 overflow-x-auto pb-4 pr-2 no-scrollbar">
                        {hourly.filter((_, i) => i % 2 === 0).map((h, i) => (
                            <div key={i} className="flex flex-col items-center min-w-[50px] gap-2">
                                <span className={`text-[10px] font-black ${h.hour === new Date().getHours() && isToday ? 'text-plantlix-500' : 'text-gray-400'}`}>
                                    {h.hour}:00
                                </span>
                                {getWeatherIcon(h.weatherCode, 18)}
                                <span className="text-xs font-black text-gray-900 dark:text-white">{h.temp}°</span>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <SunCard sunrise={dayData.sunrise} sunset={dayData.sunset} formatTime={formatTime} />
                    <section className="bg-white dark:bg-[#1a1e26] rounded-[32px] p-6 shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-white/5">
                        <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">UV-Index</h3>
                        <div className="flex items-end justify-between mb-4">
                            <div className="text-4xl font-black text-gray-900 dark:text-white leading-none">{dayData.uv}</div>
                            <div className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase ${uvInfo?.bg} ${uvInfo?.color}`}>{uvInfo?.label}</div>
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-400 via-amber-400 via-orange-400 to-rose-500" style={{ width: `${Math.min((dayData.uv / 12) * 100, 100)}%` }} />
                        </div>
                    </section>
                </div>

                {/* ── Location Map Section ─────────────────────────────────── */}
                <section className="bg-white dark:bg-[#1a1e26] rounded-[32px] p-6 shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-white/5">
                    <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <MapPin size={16} className="text-plantlix-500" /> Standort der Wetterstation
                    </h3>
                    <div className="rounded-2xl overflow-hidden grayscale-[0.5] hover:grayscale-0 transition-all duration-500">
                        <LocationMap 
                            lat={coords.lat} 
                            lng={coords.lng} 
                            height="200px"
                            zoom={12}
                        />
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                        <p className="text-[10px] font-bold text-gray-400">
                            {coords.lat.toFixed(4)}°N, {coords.lng.toFixed(4)}°E
                        </p>
                        <button 
                            onClick={fetchDayDetail}
                            className="text-[10px] font-black text-plantlix-500 uppercase tracking-wider hover:underline"
                        >
                            Standort aktualisieren
                        </button>
                    </div>
                </section>

                <div className="pt-10 flex flex-col items-center text-center gap-4 opacity-50">
                    <p className="text-[10px] text-gray-400 max-w-xs leading-relaxed font-medium">Daten von Open-Meteo. Präzise Vorhersagen für den Garten- und Landschaftsbau. Alle Angaben ohne Gewähr.</p>
                </div>
            </div>
        </div>
    );
};

const TabBtn = ({ active, onClick, icon }) => (
    <button 
        onClick={onClick}
        className={`p-2.5 rounded-lg transition-all ${active ? 'bg-white dark:bg-gray-800 text-plantlix-500 shadow-sm scale-105' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
    >
        {icon}
    </button>
);

const GlassCard = ({ icon, label, value, color }) => (
    <div className="bg-white/10 backdrop-blur-md rounded-[28px] p-4 border border-white/15 flex flex-col items-center text-center group hover:bg-white/20 transition-all shadow-lg">
        <div className={`p-2.5 rounded-2xl ${color} text-white mb-2 group-hover:scale-110 transition-transform`}>{icon}</div>
        <div className="text-[10px] font-black text-white/50 uppercase tracking-wider mb-1">{label}</div>
        <div className="text-lg font-black text-white">{value ?? '–'}</div>
    </div>
);

const SunCard = ({ sunrise, sunset, formatTime }) => (
    <section className="bg-white dark:bg-[#1a1e26] rounded-[32px] p-6 shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-white/5">
        <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6">Sonne</h3>
        <div className="flex justify-between items-center relative py-4">
            <div className="text-center">
                <Sunrise className="mx-auto mb-2 text-amber-400" size={24} />
                <p className="text-xl font-black text-gray-900 dark:text-white">{formatTime(sunrise)}</p>
                <p className="text-[10px] text-gray-400 font-bold uppercase">Aufgang</p>
            </div>
            <div className="absolute left-1/2 top-4 -translate-x-1/2 w-16 h-8 border-t-2 border-dashed border-gray-100 dark:border-white/10 rounded-t-full" />
            <div className="text-center">
                <Sunset className="mx-auto mb-2 text-orange-400" size={24} />
                <p className="text-xl font-black text-gray-900 dark:text-white">{formatTime(sunset)}</p>
                <p className="text-[10px] text-gray-400 font-bold uppercase">Untergang</p>
            </div>
        </div>
    </section>
);

export default WeatherDayDetail;
